import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { Input } from "@/components/ui/input";
import { BookUser, Plus } from "lucide-react";
import { useListKeys } from "@/lib/useListKeys";

const coaLbl = (l, lang) => (lang === "en" && l.coa_name_en) || (lang === "zh" && l.coa_name_zh) || l.coa_name;

export function LedgerInput({ clientId, value, onChange, onPick, testid = "txn-vendor", placeholder }) {
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return undefined;
    const h = setTimeout(() => {
      api.get(`/ledgers?client_id=${clientId}&q=${encodeURIComponent(value || "")}&limit=20`).then(({ data }) => setList(data)).catch(() => {});
    }, 200);
    return () => clearTimeout(h);
  }, [value, open, clientId]);

  const typed = (value || "").trim().toLowerCase();
  const exact = list.some((l) => l.name.toLowerCase() === typed);
  const keys = useListKeys(list, { open, setOpen, onPick, shown: open && (list.length > 0 || !!typed) });

  return (
    <div className="relative">
      <Input value={value} autoComplete="off" placeholder={placeholder} data-testid={testid}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} {...keys.input}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && (list.length > 0 || typed) && (
        <div ref={keys.box} className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-60 overflow-auto" data-testid={`${testid}-suggestions`}>
          {list.map((l, i) => (
            <button type="button" key={l.id} {...keys.opt(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(l); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-accent data-[active]:bg-accent transition-colors" data-testid={`ledger-opt-${l.id}`}>
              <span className="flex items-center gap-2 min-w-0"><BookUser className="h-3.5 w-3.5 text-muted-foreground shrink-0" /><span className="truncate">{l.name}</span></span>
              <span className="text-[11px] text-muted-foreground shrink-0 truncate max-w-[50%]">{l.coa_code ? `${l.coa_code} · ${coaLbl(l, lang)}` : fl("noCoa", lang)}</span>
            </button>
          ))}
          {typed && !exact && (
            <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border flex items-center gap-1.5" data-testid={`${testid}-new-hint`}>
              <Plus className="h-3 w-3" />{fl("newLedgerHint", lang)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
